<template>
    <div class="chat-input-wrap">
        <div class="chat-mention-dropdown" v-if="mentionCandidates.length">
            <div v-for="(user, idx) in mentionCandidates" :key="user._id"
                class="chat-mention-option" :class="{ active: idx == mentionIndex }"
                @mousedown.prevent="selectMention(user)">
                {{ user.name }}
            </div>
        </div>
        <div class="chat-mention-dropdown" v-else-if="showOrderDropdown">
            <div v-if="isSearchingOrders" class="chat-mention-hint">Szukam zamówień…</div>
            <template v-else>
                <div v-for="(order, idx) in orderCandidates" :key="order._id"
                    class="chat-mention-option" :class="{ active: idx == orderIndex }"
                    @mousedown.prevent="selectOrder(order)">
                    <span class="chat-order-name">#{{ order.name }}</span>
                    <small class="text-muted" v-if="order.customer && order.customer.name"> — {{ order.customer.name }}</small>
                </div>
                <div v-if="!orderCandidates.length" class="chat-mention-hint">Brak pasujących zamówień</div>
            </template>
        </div>
        <div class="chat-input-container">
            <textarea ref="textarea" class="chat-textarea" rows="1"
                v-model="draft"
                placeholder="Napisz wiadomość…"
                maxlength="4000"
                :disabled="isSending"
                @input="onInput"
                @keydown="onKeydown"></textarea>
            <a href="#" class="chat-send-btn" @click.prevent="send" :class="{ disabled: !canSend }">
                <i class="material-icons md-18">send</i>
            </a>
        </div>
    </div>
</template>

<script>
import Api from '../../Api.js';
import ChatManager from '../../ChatManager.js';
import SystemUsersManager from '../../SystemUsersManager.js';

export default {
    name: 'ChatMessageInput',
    props: {
        room: Object
    },
    data: function() {
        return {
            draft: '',
            isSending: false,
            systemUsers: [],
            mentionCandidates: [],
            mentionIndex: 0,
            mentionQueryStart: -1,      // pozycja '@' w draft
            pendingMentions: {},        // widoczna nazwa -> userId
            orderCandidates: [],
            orderIndex: 0,
            orderQueryStart: -1,        // pozycja '#' w draft
            isSearchingOrders: false,
            pendingOrders: {},          // widoczna nazwa -> orderId
        };
    },
    created: async function() {
        this.systemUsers = await SystemUsersManager.getSystemUsers();
    },
    mounted: function() {
        this.focusTextarea();
    },
    watch: {
        'room._id': function() {
            this.draft = '';
            this.pendingMentions = {};
            this.pendingOrders = {};
            this.closeMentionDropdown();
            this.closeOrderDropdown();
            this.focusTextarea();
        }
    },
    beforeDestroy: function() {
        clearTimeout(this._orderSearchTimer);
    },
    computed: {
        canSend: function() {
            return !this.isSending && this.draft.trim().length > 0;
        },
        showOrderDropdown: function() {
            return this.orderQueryStart != -1;
        }
    },
    methods: {
        focusTextarea: function() {
            this.$nextTick(() => {
                if (this.$refs.textarea) this.$refs.textarea.focus();
            });
        },
        onInput: function() {
            this.autoGrow();
            this.updateAutocomplete();
        },
        autoGrow: function() {
            const el = this.$refs.textarea;
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 120) + 'px';
        },
        // znajdź ostatni trigger ('@' lub '#') przed kursorem — na początku tekstu lub po białym znaku
        findTrigger: function(before, char) {
            const pos = before.lastIndexOf(char);
            if (pos == -1 || (pos > 0 && !/[\s\n]/.test(before[pos - 1]))) return -1;
            return pos;
        },
        updateAutocomplete: function() {
            const el = this.$refs.textarea;
            const caret = el ? el.selectionStart : this.draft.length;
            const before = this.draft.substring(0, caret);
            const atPos = this.findTrigger(before, '@');
            const hashPos = this.findTrigger(before, '#');

            if (hashPos > atPos) {
                this.closeMentionDropdown();
                this.updateOrderDropdown(before, hashPos);
            } else {
                this.closeOrderDropdown();
                this.updateMentionDropdown(before, atPos);
            }
        },
        updateMentionDropdown: function(before, atPos) {
            if (atPos == -1) return this.closeMentionDropdown();
            const query = before.substring(atPos + 1);
            if (query.length > 30 || query.includes('\n')) return this.closeMentionDropdown();

            const me = String(ChatManager.getMyUserId());
            const q = query.toLowerCase();
            this.mentionCandidates = (this.systemUsers || [])
                .filter(u => String(u._id) != me && !u.isRemoved && !u.isDisabled)
                .filter(u => (u.name || '').toLowerCase().includes(q))
                .slice(0, 6);
            this.mentionIndex = 0;
            this.mentionQueryStart = atPos;
        },
        closeMentionDropdown: function() {
            this.mentionCandidates = [];
            this.mentionIndex = 0;
            this.mentionQueryStart = -1;
        },
        updateOrderDropdown: function(before, hashPos) {
            const query = before.substring(hashPos + 1);
            if (query.length < 2 || query.length > 40 || query.includes('\n')) {
                return this.closeOrderDropdown();
            }
            // tag już rozstrzygnięty (po '#' stoi wybrana nazwa) — user pisze dalej, nie szukamy
            for (const name of Object.keys(this.pendingOrders)) {
                if (query == name || query.startsWith(name + ' ')) return this.closeOrderDropdown();
            }
            this.orderQueryStart = hashPos;
            if (query == this._lastOrderQuery) return; // ruch kursora bez zmiany zapytania
            this._lastOrderQuery = query;
            this.searchOrders(query);
        },
        searchOrders: function(query) {
            this.isSearchingOrders = true;
            clearTimeout(this._orderSearchTimer);
            const seq = this._orderSearchSeq = (this._orderSearchSeq || 0) + 1;
            this._orderSearchTimer = setTimeout(async () => {
                try {
                    const res = await Api.order.getOrders({ query: query, limit: 6 });
                    if (seq != this._orderSearchSeq) return; // nieaktualne zapytanie
                    this.orderCandidates = (res.data || []).slice(0, 6);
                } catch (e) {
                    if (seq == this._orderSearchSeq) this.orderCandidates = [];
                } finally {
                    if (seq == this._orderSearchSeq) {
                        this.isSearchingOrders = false;
                        this.orderIndex = 0;
                    }
                }
            }, 300);
        },
        closeOrderDropdown: function() {
            clearTimeout(this._orderSearchTimer);
            this._orderSearchSeq = (this._orderSearchSeq || 0) + 1;
            this._lastOrderQuery = null;
            this.orderCandidates = [];
            this.orderIndex = 0;
            this.orderQueryStart = -1;
            this.isSearchingOrders = false;
        },
        selectMention: function(user) {
            const start = this.mentionQueryStart;
            this.insertAtTrigger(start, '@' + user.name + ' ');
            this.$set(this.pendingMentions, user.name, String(user._id));
            this.closeMentionDropdown();
        },
        selectOrder: function(order) {
            // nazwa trafi do tokenu '#[o:<id>|<nazwa>]' — bez nawiasów kwadratowych i nowych linii
            const cleanName = String(order.name || '').replace(/[[\]\n]/g, ' ').replace(/\s+/g, ' ').trim();
            if (!cleanName) return this.closeOrderDropdown();
            const start = this.orderQueryStart;
            this.insertAtTrigger(start, '#' + cleanName + ' ');
            this.$set(this.pendingOrders, cleanName, String(order._id));
            this.closeOrderDropdown();
        },
        insertAtTrigger: function(start, inserted) {
            const el = this.$refs.textarea;
            const caret = el ? el.selectionStart : this.draft.length;
            this.draft = this.draft.substring(0, start) + inserted + this.draft.substring(caret);
            this.$nextTick(() => {
                if (el) {
                    el.focus();
                    const pos = start + inserted.length;
                    el.setSelectionRange(pos, pos);
                }
                this.autoGrow();
            });
        },
        onKeydown: function(e) {
            const candidates = this.mentionCandidates.length ? this.mentionCandidates : this.orderCandidates;
            const isOrder = !this.mentionCandidates.length && this.showOrderDropdown;
            if (candidates.length || isOrder) {
                if (e.key == 'Escape') {
                    this.closeMentionDropdown();
                    this.closeOrderDropdown();
                    return;
                }
            }
            if (candidates.length) {
                const idxKey = isOrder ? 'orderIndex' : 'mentionIndex';
                if (e.key == 'ArrowDown') {
                    e.preventDefault();
                    this[idxKey] = (this[idxKey] + 1) % candidates.length;
                    return;
                }
                if (e.key == 'ArrowUp') {
                    e.preventDefault();
                    this[idxKey] = (this[idxKey] - 1 + candidates.length) % candidates.length;
                    return;
                }
                if (e.key == 'Enter' || e.key == 'Tab') {
                    e.preventDefault();
                    if (isOrder) this.selectOrder(candidates[this.orderIndex]);
                    else this.selectMention(candidates[this.mentionIndex]);
                    return;
                }
            }
            if (e.key == 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.send();
            }
        },
        // Konwersja widocznych '@Imię Nazwisko' na tokeny '@[u:<id>]' + budowa listy mentions,
        // oraz '#Nazwa zamówienia' na tokeny '#[o:<id>|<nazwa>]'
        buildOutgoing: function() {
            let text = this.draft;
            const mentions = [];
            // najdłuższe nazwy najpierw, żeby '@Jan Kowalski' nie został zjedzony przez '@Jan'
            const names = Object.keys(this.pendingMentions).sort((a, b) => b.length - a.length);
            for (const name of names) {
                const id = this.pendingMentions[name];
                const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const re = new RegExp('@' + escapedName, 'g');
                if (re.test(text)) {
                    text = text.replace(re, '@[u:' + id + ']');
                    mentions.push(id);
                }
            }
            const orderNames = Object.keys(this.pendingOrders).sort((a, b) => b.length - a.length);
            for (const name of orderNames) {
                const id = this.pendingOrders[name];
                const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const re = new RegExp('#' + escapedName, 'g');
                if (re.test(text)) {
                    text = text.replace(re, '#[o:' + id + '|' + name + ']');
                }
            }
            return { text: text.trim(), mentions };
        },
        send: async function() {
            if (!this.canSend) return;
            const { text, mentions } = this.buildOutgoing();
            if (!text) return;
            this.isSending = true;
            try {
                await ChatManager.send(this.room._id, text, mentions);
                this.draft = '';
                this.pendingMentions = {};
                this.pendingOrders = {};
                this.closeMentionDropdown();
                this.closeOrderDropdown();
                this.$nextTick(this.autoGrow);
            } catch (e) {
                this.$h.commonApiError(e);
            } finally {
                this.isSending = false;
                this.$nextTick(() => { if (this.$refs.textarea) this.$refs.textarea.focus(); });
            }
        }
    }
};
</script>

<style scoped lang="scss">
.chat-input-wrap {
    position: relative;
    flex-shrink: 0;
    padding: 10px 12px;
    border-top: 1px solid var(--dm-border, #e9ecef);
    background: var(--dm-surface, #fff);
}

.chat-mention-dropdown {
    position: absolute;
    bottom: 100%;
    left: 12px;
    right: 12px;
    margin-bottom: 4px;
    background: var(--dm-surface, #fff);
    border: 1px solid var(--dm-border, #ddd);
    border-radius: 10px;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    z-index: 10;
}

.chat-mention-option {
    padding: 7px 12px;
    font-size: 13px;
    cursor: pointer;

    &:hover, &.active { background: var(--dm-surface-2, #eef4ff); }
}

.chat-mention-hint {
    padding: 7px 12px;
    font-size: 12.5px;
    opacity: 0.6;
}

.chat-order-name {
    font-weight: 600;
}

.chat-input-container {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    background: var(--dm-surface, #fff);
    border: 1px solid var(--dm-border, #dde3ea);
    border-radius: 12px;
    padding: 7px 7px 7px 14px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
    }
}

.chat-textarea {
    flex: 1;
    border: none;
    outline: none;
    resize: none;
    background: transparent;
    color: inherit;
    font-size: 13.5px;
    line-height: 1.45;
    max-height: 120px;
    font-family: inherit;
    padding: 5px 0;

    &::placeholder { color: var(--dm-text-muted, #9aa3ad); }
}

.chat-send-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #007bff;
    color: #fff;
    transition: background 0.15s ease, transform 0.1s ease;

    &:hover { background: #0069d9; color: #fff; }
    &:active { transform: scale(0.92); }

    &.disabled {
        background: var(--dm-surface-2, #eceff3);
        color: var(--dm-text-muted, #a8b0ba);
        pointer-events: none;
    }
}
</style>
